//hooks
import { useEffect, useMemo, useState } from "react";
import { useDashboardFilter } from "../contexts/DashboardFilterContext";
import {
  useTheme,
  Box,
  Checkbox,
  TextField,
  Typography,
  Autocomplete,
  FormControlLabel,
} from "@mui/material";

//types
import {
  Filter,
  DashboardControls,
  Option,
  ValueFilter,
  ValuesFilter,
  CheckboxFilter,
} from "../utils/types";
import { CustomChartProps } from "../modules/charts/types";

//utils
import { baseUrl } from "../config";
import axiosConfig from "../utils/axiosConfig";
import { sortAsc } from "../utils/common";

export interface IDashboardFilterFieldProps {
  readonly filter: Filter;
  readonly charts: CustomChartProps[];
  readonly dashboardId: number;
  readonly dashboardControls: DashboardControls;
}

export default function DashboardFilterField({
  filter,
  charts,
  dashboardId,
  dashboardControls,
}: IDashboardFilterFieldProps) {
  const theme = useTheme();

  const { id, type, name, required } = filter;
  const { filters, filterValues, lastUpdatedFilter, updateFilterValue } =
    useDashboardFilter();

  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<Option[]>([]);

  const isValueValid = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string" || typeof value === "number") {
      return value !== "";
    }
    if (typeof value === "object") {
      return "length" in value && value.length > 0;
    }
    if (typeof value === "boolean") {
      return value;
    }
    return false;
  };

  const isValueDisabling = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string" || typeof value === "number") {
      return value === "";
    }
    if (typeof value === "object") {
      return !("length" in value && value.length > 0);
    }
    if (typeof value === "boolean") {
      return !value;
    }
    return true;
  };

  const enabled = useMemo<boolean>(() => {
    const hasEveryRequiredFitlerValues = filter.requiredFilterIds.every(
      (filterId) => isValueValid(filterValues[filterId])
    );

    if (!filter.disablingFilterIds) return hasEveryRequiredFitlerValues;

    const hasSomeDisablingFilterValues = filter.disablingFilterIds.some(
      (filterId) => isValueDisabling(filterValues[filterId])
    );

    return hasEveryRequiredFitlerValues && hasSomeDisablingFilterValues;
  }, [filter, filterValues]);

  const fetchOptionsForChart = async (
    chartId: string,
    newDatasetId: string
  ) => {
    const body = {
      dashboard_id: dashboardId,
      filter_column: filter.column,
      filters: filter.cascadedFilterIds
        .map((_filterId) => {
          const _filter = filters.find((_fltr) => _fltr.id === _filterId)!;
          const value = filterValues[_filterId];

          let whereClause = null;
          if (_filter.type === "value") {
            whereClause = _filter.getWhereClause(_filter as ValueFilter, value);
          } else if (_filter.type === "values") {
            whereClause = _filter.getWhereClause(
              _filter as ValuesFilter,
              value
            );
          } else {
            whereClause = _filter.getWhereClause(
              _filter as CheckboxFilter,
              value
            );
          }
          return whereClause;
        })
        .filter((whereClause) => whereClause !== null),
      pagination: null,
    };

    const fetchDataOption = {
      method: "POST",
      url: `${baseUrl}/explore_chart_data/${newDatasetId}`,
      data: body,
    };

    return axiosConfig(fetchDataOption);
  };

  const processOptions = (results: any[]): Option[] => {
    const _options: Option[] = results.reduce((acc, result) => {
      if (result.data.rows && result.data.rows.length > 0) {
        return acc.concat(
          result.data.rows.map((row: any) => row[filter.column])
        );
      }
      return acc;
    }, []);

    let uniqOptions = Array.from(
      new Set(_options.filter((_opt) => _opt !== null))
    ).sort(sortAsc);

    if ("formatOptions" in filter && filter.formatOptions) {
      uniqOptions = filter.formatOptions(uniqOptions);
    }

    return uniqOptions;
  };

  const shouldFetchOptions = (): boolean => {
    if (!enabled) return false;
    if (filter.type !== "value" && filter.type !== "values") return false;
    if ("options" in filter && filter.options !== null) return false;
    if (
      lastUpdatedFilter &&
      !filter.cascadedFilterIds.includes(lastUpdatedFilter.id)
    )
      return false;
    return true;
  };

  const getChartPromise = (chartId: string) => {
    const chart = charts.find((_chart) => _chart.id === chartId)!;
    let newDatasetId = chart.datasetId;
    if (dashboardControls["datasetSwitchControl"]) {
      newDatasetId = dashboardControls["datasetSwitchControl"].value;
    }
    return fetchOptionsForChart(chartId, newDatasetId);
  };

  useEffect(() => {
    if (!shouldFetchOptions()) {
      if ("options" in filter && filter.options !== null) {
        setOptions(filter.options);
      }
      return;
    }

    const fetchOptions = async () => {
      setLoading(true);
      try {
        const promises = filter.appliesToChartIds.map(getChartPromise);
        const results = await Promise.all(promises);
        const processedOptions = processOptions(results);
        setOptions(processedOptions);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();

    return () => {
      setLoading(false);
    };
  }, [
    charts,
    enabled,
    filter,
    filters,
    lastUpdatedFilter,
    dashboardId,
    dashboardControls,
  ]);

  return (
    <Box>
      {(type === "value" || type === "values") && (
        <Autocomplete
          multiple={type === "values"}
          id={filter.id}
          size="small"
          disabled={!enabled}
          options={options}
          filterSelectedOptions
          value={filterValues[id]}
          onChange={(event: any, newValue: any) =>
            updateFilterValue(id, newValue)
          }
          renderInput={(params) => (
            <TextField
              required={required}
              {...params}
              label={name}
              placeholder={loading ? `Loading...` : `${options.length} Options`}
            />
          )}
        />
      )}
      {type === "checkbox" && (
        <FormControlLabel
          required={required}
          disabled={!enabled}
          control={
            <Checkbox
              checked={filterValues[id]}
              onChange={(e) => updateFilterValue(id, e.target.checked)}
              sx={{ py: 0 }}
            />
          }
          label={
            <Typography
              fontSize={14}
              sx={{
                color: enabled
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
              }}
            >
              {name}
            </Typography>
          }
        />
      )}
    </Box>
  );
}
